import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaUsers, FaUserTie, FaSpinner, FaCog, FaTimes,
  FaChartBar, FaClipboardList, FaBook, FaBell, FaFileAlt, FaUserShield, FaHandshake, FaUserClock
} from 'react-icons/fa';
import axios from 'axios';
import {
  AdminOverviewTab, AdminManagersTab, AdminAdvisorsTab, AdminClientsTab,
  AdminContentTab, AdminNotificationsTab, AdminAuditTab, AdminSettingsTab, AdminReportsTab,
  AddManagerModal, AddContentModal, SendNotificationModal
} from '../components/admin';
import AdvisorReferralsAdmin from '../components/admin/AdvisorReferralsAdmin';
import PendingClientApprovals from '../components/admin/PendingClientApprovals';
import AdminGrowthPartnersTab from '../components/admin/AdminGrowthPartnersTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  
  // Data states
  const [systemStats, setSystemStats] = useState(null);
  const [managers, setManagers] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [clientsOverview, setClientsOverview] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [settings, setSettings] = useState({});
  const [content, setContent] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [topUpRequests, setTopUpRequests] = useState([]);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [monthlyReport, setMonthlyReport] = useState(null);
  
  // Modal states
  const [showAddManager, setShowAddManager] = useState(false);
  const [showAddContent, setShowAddContent] = useState(false);
  const [showSendNotification, setShowSendNotification] = useState(false);
  
  // Form states
  const [newManager, setNewManager] = useState({ name: '', email: '', phone: '', password: '' });
  const [newContent, setNewContent] = useState({ title: '', description: '', content_type: 'youtube', external_url: '', category: 'training', visible_to: ['advisor', 'manager'] });
  const [newNotification, setNewNotification] = useState({ title: '', message: '', notification_type: 'info', target_roles: ['advisor', 'manager'] });
  const [submitting, setSubmitting] = useState(false);

  const getToken = useCallback(() => localStorage.getItem('token'), []);
  const headers = useCallback(() => ({ Authorization: `Bearer ${getToken()}` }), [getToken]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const hdrs = headers();
    try {
      switch (activeTab) {
        case 'overview':
          const [statsRes, requestsRes, topupRes, pendingRegRes] = await Promise.all([
            axios.get(`${API}/admin/system-stats`, { headers: hdrs }),
            axios.get(`${API}/admin/advisor-requests`, { headers: hdrs }).catch(() => ({ data: [] })),
            axios.get(`${API}/admin/topup-requests`, { headers: hdrs }).catch(() => ({ data: [] })),
            axios.get(`${API}/admin/pending-registrations`, { headers: hdrs }).catch(() => ({ data: [] }))
          ]);
          setSystemStats(statsRes.data);
          setPendingRequests(requestsRes.data || []);
          setTopUpRequests(topupRes.data || []);
          setPendingRegistrations(pendingRegRes.data || []);
          break;
        case 'managers':
          const managersRes = await axios.get(`${API}/admin/managers`, { headers: hdrs });
          setManagers(managersRes.data);
          break;
        case 'advisors': {
          // Need managers list too so super-admin can map advisor → manager
          const [advisorsRes, managersRes] = await Promise.all([
            axios.get(`${API}/admin/all-advisors`, { headers: hdrs }),
            axios.get(`${API}/admin/managers`, { headers: hdrs }).catch(() => ({ data: [] })),
          ]);
          setAdvisors(advisorsRes.data);
          setManagers(managersRes.data || []);
          break;
        }
        case 'clients': {
          // Need advisors list too so super-admin can map client → advisor
          const [clientsRes, advisorsForClientsRes] = await Promise.all([
            axios.get(`${API}/admin/clients-overview`, { headers: hdrs }),
            axios.get(`${API}/admin/all-advisors`, { headers: hdrs }).catch(() => ({ data: [] })),
          ]);
          setClientsOverview(clientsRes.data);
          setAdvisors(advisorsForClientsRes.data || []);
          break;
        }
        case 'audit':
          const auditRes = await axios.get(`${API}/admin/audit-logs?limit=200`, { headers: hdrs });
          setAuditLogs(auditRes.data);
          break;
        case 'settings':
          const settingsRes = await axios.get(`${API}/admin/settings`, { headers: hdrs });
          setSettings(settingsRes.data);
          break;
        case 'content':
          const contentRes = await axios.get(`${API}/admin/content`, { headers: hdrs });
          setContent(contentRes.data);
          break;
        case 'notifications':
          const notifRes = await axios.get(`${API}/admin/notifications`, { headers: hdrs });
          setNotifications(notifRes.data);
          break;
        case 'reports':
          const reportRes = await axios.get(`${API}/admin/reports/monthly`, { headers: hdrs });
          setMonthlyReport(reportRes.data);
          break;
        default:
          break;
      }
    } catch (error) { /* silenced */ }
    finally { setLoading(false); }
  }, [activeTab, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Manager actions
  const handleCreateManager = async () => {
    if (!newManager.name || !newManager.email || !newManager.password) {
      setMessage({ type: 'error', text: 'Please fill all required fields' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/managers`, newManager, { headers: headers() });
      setMessage({ type: 'success', text: 'Manager created successfully!' });
      setShowAddManager(false);
      setNewManager({ name: '', email: '', phone: '', password: '' });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to create manager' }); }
    finally { setSubmitting(false); }
  };

  const handleToggleManagerStatus = async (manager) => {
    try {
      const endpoint = manager.is_active ? 'deactivate' : 'activate';
      await axios.post(`${API}/admin/managers/${manager.id}/${endpoint}`, {}, { headers: headers() });
      setMessage({ type: 'success', text: `Manager ${endpoint}d successfully` });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update manager' }); }
  };

  const handleToggleAdvisorStatus = async (advisor) => {
    try {
      const endpoint = advisor.is_active ? 'deactivate' : 'activate';
      await axios.post(`${API}/admin/advisors/${advisor.id}/${endpoint}`, {}, { headers: headers() });
      setMessage({ type: 'success', text: `Advisor ${endpoint}d successfully` });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update advisor' }); }
  };

  // Super-admin mapping actions
  const handleMapAdvisorToManager = async (advisorId, managerId) => {
    await axios.post(
      `${API}/admin/assign-advisor-manager`,
      { advisor_id: advisorId, manager_id: managerId || null },
      { headers: headers() }
    );
    setMessage({
      type: 'success',
      text: managerId ? 'Advisor mapped to manager successfully' : 'Advisor unmapped (now independent)',
    });
    fetchData();
  };

  const handleMapClientToAdvisor = async (clientId, advisorId) => {
    await axios.post(
      `${API}/admin/assign-client-advisor`,
      { client_id: clientId, advisor_id: advisorId || null },
      { headers: headers() }
    );
    setMessage({
      type: 'success',
      text: advisorId ? 'Client mapped to advisor successfully' : 'Client unmapped from advisor',
    });
    fetchData();
  };

  // Content actions
  const handleCreateContent = async () => {
    if (!newContent.title || !newContent.external_url) {
      setMessage({ type: 'error', text: 'Please fill all required fields' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/content`, newContent, { headers: headers() });
      setMessage({ type: 'success', text: 'Content added successfully!' });
      setShowAddContent(false);
      setNewContent({ title: '', description: '', content_type: 'youtube', external_url: '', category: 'training', visible_to: ['advisor', 'manager'] });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to add content' }); }
    finally { setSubmitting(false); }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    try {
      await axios.delete(`${API}/admin/content/${contentId}`, { headers: headers() });
      setMessage({ type: 'success', text: 'Content deleted successfully' });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to delete content' }); }
  };

  // Notification actions
  const handleSendNotification = async () => {
    if (!newNotification.title || !newNotification.message) {
      setMessage({ type: 'error', text: 'Please fill all required fields' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/notifications`, newNotification, { headers: headers() });
      setMessage({ type: 'success', text: 'Notification sent successfully!' });
      setShowSendNotification(false);
      setNewNotification({ title: '', message: '', notification_type: 'info', target_roles: ['advisor', 'manager'] });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to send notification' }); }
    finally { setSubmitting(false); }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/admin/notifications/${notificationId}`, { headers: headers() });
      setMessage({ type: 'success', text: 'Notification deleted' });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to delete notification' }); }
  };

  // Settings actions
  const handleSaveSettings = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, { headers: headers() });
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save settings' }); }
    finally { setSubmitting(false); }
  };

  // Top-up & Advisor approval
  const handleApproveTopUp = async (request) => {
    try {
      await axios.post(`${API}/admin/approve-topup/${request.id}`, {}, { headers: headers() });
      setMessage({ type: 'success', text: `Top-up approved for ${request.advisor_name}!` });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to approve top-up' }); }
  };

  const handleRejectTopUp = async (request) => {
    try {
      await axios.post(`${API}/admin/reject-topup/${request.id}`, { reason: '' }, { headers: headers() });
      setMessage({ type: 'success', text: `Top-up request rejected` });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to reject top-up' }); }
  };

  const handleApproveAdvisorRequest = async (request) => {
    const password = prompt('Enter password for new advisor:');
    if (!password) return;
    try {
      await axios.post(`${API}/admin/approve-advisor/${request.id}`, { password }, { headers: headers() });
      setMessage({ type: 'success', text: `Advisor ${request.name} approved!` });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to approve request' }); }
  };

  // Pending User Registration handlers
  const handleApproveRegistration = async (user) => {
    if (!window.confirm(`Are you sure you want to approve ${user.name} (${user.role})?`)) return;
    try {
      await axios.post(`${API}/admin/registrations/${user.id}/approve`, {}, { headers: headers() });
      setMessage({ type: 'success', text: `${user.name} has been approved and can now log in!` });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to approve registration' }); }
  };

  const handleRejectRegistration = async (user) => {
    const reason = prompt('Enter rejection reason (optional):');
    if (reason === null) return; // User cancelled
    try {
      await axios.post(`${API}/admin/registrations/${user.id}/reject`, { reason: reason || 'Registration not approved.' }, { headers: headers() });
      setMessage({ type: 'success', text: `Registration for ${user.name} has been rejected.` });
      fetchData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to reject registration' }); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FaChartBar },
    { id: 'client-approvals', label: 'Client Approvals', icon: FaUserClock },
    { id: 'managers', label: 'Managers', icon: FaUserTie },
    { id: 'advisors', label: 'Advisors', icon: FaUsers },
    { id: 'clients', label: 'Clients', icon: FaUsers },
    { id: 'advisor-referrals', label: 'Advisor Referrals', icon: FaHandshake },
    { id: 'growth-partners', label: 'Growth Partners', icon: FaHandshake },
    { id: 'content', label: 'Content', icon: FaBook },
    { id: 'notifications', label: 'Notifications', icon: FaBell },
    { id: 'audit', label: 'Audit Logs', icon: FaClipboardList },
    { id: 'settings', label: 'Settings', icon: FaCog },
  ];

  const renderTabContent = () => {
    if (loading) return <div className="flex items-center justify-center h-64"><FaSpinner className="animate-spin text-[#FFD700] text-4xl" /></div>;

    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <AdminOverviewTab 
              systemStats={systemStats} 
              pendingRequests={pendingRequests} 
              topUpRequests={topUpRequests} 
              pendingRegistrations={pendingRegistrations}
              handleApproveAdvisorRequest={handleApproveAdvisorRequest} 
              handleApproveTopUp={handleApproveTopUp} 
              handleRejectTopUp={handleRejectTopUp}
              handleApproveRegistration={handleApproveRegistration}
              handleRejectRegistration={handleRejectRegistration}
            />
            <PendingClientApprovals />
          </div>
        );
      case 'client-approvals':
        return <PendingClientApprovals />;
      case 'managers':
        return <AdminManagersTab managers={managers} handleToggleManagerStatus={handleToggleManagerStatus} setShowAddManager={setShowAddManager} />;
      case 'advisors':
        return <AdminAdvisorsTab advisors={advisors} managers={managers} handleToggleAdvisorStatus={handleToggleAdvisorStatus} onMapManager={handleMapAdvisorToManager} />;
      case 'clients':
        return <AdminClientsTab clientsOverview={clientsOverview} advisors={advisors} onMapAdvisor={handleMapClientToAdvisor} />;
      case 'advisor-referrals':
        return <AdvisorReferralsAdmin />;
      case 'growth-partners':
        return <AdminGrowthPartnersTab />;
      case 'content':
        return <AdminContentTab content={content} handleDeleteContent={handleDeleteContent} setShowAddContent={setShowAddContent} />;
      case 'notifications':
        return <AdminNotificationsTab notifications={notifications} handleDeleteNotification={handleDeleteNotification} setShowSendNotification={setShowSendNotification} />;
      case 'audit':
        return <AdminAuditTab auditLogs={auditLogs} />;
      case 'settings':
        return <AdminSettingsTab settings={settings} setSettings={setSettings} handleSaveSettings={handleSaveSettings} submitting={submitting} />;
      case 'reports':
        return <AdminReportsTab monthlyReport={monthlyReport} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      {/* Message Alert */}
      {message && (
        <div className={`p-4 rounded-lg border ${message.type === 'success' ? 'bg-green-500/20 border-green-500 text-green-400' : 'bg-red-500/20 border-red-500 text-red-400'}`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="float-right hover:opacity-70"><FaTimes /></button>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center space-x-4">
          <FaUserShield className="text-[#FFD700] text-3xl" />
          <div>
            <h1 className="text-2xl font-bold text-white">Super Admin Dashboard</h1>
            <p className="text-gray-400">Manage your platform</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-2 border border-[#2D5A8F]">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold'
                  : 'text-gray-300 hover:bg-[#2D5A8F]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon />
              <span className="hidden md:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Modals */}
      <AddManagerModal
        show={showAddManager}
        newManager={newManager}
        setNewManager={setNewManager}
        handleCreate={handleCreateManager}
        submitting={submitting}
        onClose={() => { setShowAddManager(false); setNewManager({ name: '', email: '', phone: '', password: '' }); }}
      />
      <AddContentModal
        show={showAddContent}
        newContent={newContent}
        setNewContent={setNewContent}
        handleCreate={handleCreateContent}
        submitting={submitting}
        onClose={() => setShowAddContent(false)}
      />
      <SendNotificationModal
        show={showSendNotification}
        newNotification={newNotification}
        setNewNotification={setNewNotification}
        handleSend={handleSendNotification}
        submitting={submitting}
        onClose={() => setShowSendNotification(false)}
      />
    </div>
  );
};

export default SuperAdminDashboard;
